import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { inform } from "./helpers";
import { STRINGS } from "./declarations";

/**
 * Public origin used in shared links. Always the production web app, even
 * when shared from the native app, so the link opens for anyone (and, on a
 * phone with Carata installed, opens the app through the app/universal link).
 */
export const SHARE_ORIGIN = "https://app.carata.co";

// Links use the readable key (product slug / business handle). The id is only
// a fallback for records that have no usable slug/handle, or whose slug/handle
// collides with a reserved word the API/nginx treat specially.
const SAFE_KEY = /^[A-Za-z0-9._-]+$/;
const RESERVED_KEYS = new Set([
    "check-availability",
    "search",
    "create",
    "bookmarked",
]);

const linkKey = (preferred, fallback) =>
    typeof preferred === "string" &&
    SAFE_KEY.test(preferred) &&
    !RESERVED_KEYS.has(preferred.toLowerCase())
        ? preferred
        : fallback;

/** @param {{ slug?: string, id: number|string }} product */
export const productShareUrl = (product) =>
    `${SHARE_ORIGIN}/products/${linkKey(product.slug, product.id)}`;

/** @param {{ handle?: string, id: number|string }} company */
export const businessShareUrl = (company) =>
    `${SHARE_ORIGIN}/business/${linkKey(company.handle, company.id)}`;

/**
 * Only PUBLIC products can be shared. A product with a `user_id` belongs to a
 * person (private). `"user_id" in product` makes sure we've got the full
 * record, so a partial list payload never briefly shows a Share button for a
 * private product.
 */
export function isShareableProduct(product) {
    return (
        !!product?.id &&
        "user_id" in product &&
        (product.user_id === null ||
            product.user_id === undefined ||
            product.user_id === "")
    );
}

/**
 * Opens the system share sheet (WhatsApp, Messages, ...) for a link.
 * Native app -> Capacitor Share; browser/PWA -> Web Share API; otherwise the
 * link is copied to the clipboard.
 *
 * @param {{ title?: string, text?: string, url: string }} content
 */
export async function shareLink({ title, text, url }) {
    try {
        if (Capacitor.isNativePlatform()) {
            await Share.share({
                title,
                text,
                url,
                dialogTitle: "Share with",
            });
            return;
        }

        if (navigator.share) {
            await navigator.share({ title, text, url });
            return;
        }

        await navigator.clipboard.writeText(url);
        inform("Link copied", STRINGS.SUCCESS);
    } catch (e) {
        // The user closing the share sheet is not an error.
        const message = String(e?.message ?? e ?? "").toLowerCase();
        if (e?.name === "AbortError" || message.includes("cancel")) return;

        // Last resort: try the clipboard before giving up.
        try {
            await navigator.clipboard.writeText(url);
            inform("Link copied", STRINGS.SUCCESS);
        } catch (_) {
            inform("Couldn't share this link", STRINGS.ERROR);
        }
    }
}
